import React from "react";
import { Link } from "react-router-dom";
import Select from "react-select";

const formatParentOption = (option) => (
  <div className="d-flex justify-content-between align-items-center">
    <span>{option.label}</span>
    <small className="text-muted ms-2">
      {option.disabled_reason || option.type || ""}
    </small>
  </div>
);

export const capitalize = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

// Fields shared by the create and edit screens; state and saving stay with
// the page.
const CostCenterForm = ({
  inputs,
  options,
  selectedParent,
  selectedDepartment,
  selectedCustomer,
  onChange,
  onParentChange,
  onDepartmentChange,
  onCustomerChange,
  onSubmit,
  saving,
  submitLabel,
  codeHint,
  parentHint,
}) => (
  <>
    <div className="coa-panel">
      <div className="coa-panel-head">
        <span>
          <i className="fa fa-bullseye me-2"></i>Cost Center Identity
        </span>
        <span
          className={`coa-pill ${
            inputs.status === "active" ? "coa-pill-dr" : "coa-pill-muted"
          }`}
        >
          {capitalize(inputs.status)}
        </span>
      </div>
      <div className="coa-panel-body">
        <div className="row">
          <div className="col-lg-3">
            <label>
              Code <i className="fa fa-star required"></i>
            </label>
            <input
              type="text"
              className="form-control coa-mono"
              name="code"
              value={inputs.code}
              onChange={onChange}
            />
            {codeHint && <small className="coa-hint">{codeHint}</small>}
          </div>
          <div className="col-lg-5 mg-t-10 mg-lg-t-0">
            <label>
              Name <i className="fa fa-star required"></i>
            </label>
            <input
              type="text"
              className="form-control"
              name="name"
              value={inputs.name}
              onChange={onChange}
              placeholder="e.g. Head Office Operations"
            />
          </div>
          <div className="col-lg-2 mg-t-10 mg-lg-t-0">
            <label>
              Type <i className="fa fa-star required"></i>
            </label>
            <select
              className="form-control"
              name="type"
              value={inputs.type}
              onChange={onChange}
            >
              {options.types.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          <div className="col-lg-2 mg-t-10 mg-lg-t-0">
            <label>Status</label>
            <select
              className="form-control"
              name="status"
              value={inputs.status}
              onChange={onChange}
            >
              {options.statuses?.map((status) => (
                <option key={status} value={status}>
                  {capitalize(status)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>

    <div className="coa-panel">
      <div className="coa-panel-head">
        <span>
          <i className="fa fa-sitemap me-2"></i>Hierarchy &amp; Ownership
        </span>
        <span className="coa-head-note">All optional</span>
      </div>
      <div className="coa-panel-body">
        <div className="row">
          <div className="col-lg-4">
            <label>Parent Cost Center</label>
            <Select
              className="basic-single"
              classNamePrefix="select"
              placeholder="No parent (top level)"
              name="parent_id"
              options={options.parents}
              value={selectedParent}
              onChange={onParentChange}
              formatOptionLabel={formatParentOption}
              isOptionDisabled={(option) => option.isDisabled}
              maxMenuHeight={320}
              isClearable
              isSearchable
            />
            {parentHint && <small className="coa-hint">{parentHint}</small>}
          </div>
          <div className="col-lg-4 mg-t-10 mg-lg-t-0">
            <label>Department</label>
            <Select
              className="basic-single"
              classNamePrefix="select"
              placeholder="Select Department"
              name="department_id"
              options={options.departments}
              value={selectedDepartment}
              onChange={onDepartmentChange}
              maxMenuHeight={320}
              isClearable
              isSearchable
            />
          </div>
          <div className="col-lg-4 mg-t-10 mg-lg-t-0">
            <label>Customer</label>
            <Select
              className="basic-single"
              classNamePrefix="select"
              placeholder="Internal (no customer)"
              name="acc_customer_id"
              options={options.customers}
              value={selectedCustomer}
              onChange={onCustomerChange}
              maxMenuHeight={320}
              isClearable
              isSearchable
            />
            <small className="coa-hint">
              One customer can own many cost centers.
            </small>
          </div>
        </div>
      </div>
    </div>

    <div className="coa-panel">
      <div className="coa-panel-head">
        <span>
          <i className="fa fa-money me-2"></i>Budget &amp; Remarks
        </span>
      </div>
      <div className="coa-panel-body">
        <div className="row">
          <div className="col-lg-3">
            <label>Effective Date</label>
            <input
              type="date"
              className="form-control"
              name="effective_date"
              value={inputs.effective_date || ""}
              onChange={onChange}
            />
          </div>
          <div className="col-lg-3 mg-t-10 mg-lg-t-0">
            <label>Budget Amount</label>
            <input
              type="number"
              step="0.01"
              className="form-control coa-num"
              name="budget_amount"
              value={inputs.budget_amount}
              onChange={onChange}
              placeholder="0.00"
            />
          </div>
          <div className="col-lg-6 mg-t-10 mg-lg-t-0">
            <label>Remarks</label>
            <textarea
              className="form-control"
              name="description"
              rows="1"
              value={inputs.description || ""}
              onChange={onChange}
              placeholder="What this cost center tracks"
            />
          </div>
        </div>
      </div>
    </div>

    <div className="d-flex justify-content-between align-items-center">
      <Link to="/cost-center" className="btn btn-outline-secondary">
        Cancel
      </Link>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onSubmit}
        disabled={saving}
      >
        <i
          className={`fa ${saving ? "fa-spinner fa-spin" : "fa-send"} mg-r-10`}
        ></i>
        {saving ? "Saving..." : submitLabel}
      </button>
    </div>
  </>
);

export default CostCenterForm;
